/**
 * 权限码常量定义
 *
 * 统一管理所有权限码，确保前后端一致性。
 * 与后端 app/core/permission_codes.py 保持同步。
 *
 * 命名规范：module:resource:action
 * 已发布契约码（classroom:* / classroom_v3:* / speech_translation:*）禁止改名。
 */

export const PermissionCode = {
  // ==================== 项目管理模块 ====================
  PROJECT_LIST: 'project:project:list',
  PROJECT_VIEW: 'project:project:view',
  PROJECT_CREATE: 'project:project:create',
  PROJECT_EDIT: 'project:project:edit',
  PROJECT_DELETE: 'project:project:delete',
  PROJECT_EXPORT: 'project:project:export',

  // ==================== 视频译配模块 ====================
  VIDEO_UPLOAD: 'video:file:upload',
  VIDEO_SUBTITLE: 'video:subtitle:edit',
  VIDEO_TRANSLATE: 'video:translation:edit',
  VIDEO_ASSIGN: 'video:task:assign',

  // ==================== 文档翻译模块 ====================
  DOCUMENT_UPLOAD: 'document:file:upload',
  DOCUMENT_TRANSLATE: 'document:file:translate',
  DOCUMENT_EDIT: 'document:file:edit',
  DOCUMENT_VIEW_RESULTS: 'document:result:view',
  DOCUMENT_ASSIGN: 'document:task:assign',

  // ==================== 任务管理模块 ====================
  TASK_VIEW: 'task:item:view',
  TASK_CREATE: 'task:item:create',
  TASK_ASSIGN: 'task:item:assign',
  TASK_COMPLETE: 'task:item:complete',
  TASK_EDIT: 'task:item:edit',

  // ==================== 系统管理模块 ====================
  SYSTEM_USER: 'system:user:manage',
  SYSTEM_ROLE: 'system:role:manage',
  SYSTEM_PERMISSION: 'system:permission:manage',
  SYSTEM_ORGANIZATION: 'system:organization:manage',
  SYSTEM_USAGE: 'system:usage:manage',
  SYSTEM_API: 'system:api:manage',
  SYSTEM_DICTIONARY: 'system:dictionary:manage',
  SYSTEM_CONFIG: 'system:config:manage',

  // ==================== 会议翻译模块（契约码，禁止改名） ====================
  SPEECH_TRANSLATION_USE: 'speech_translation:use',
  SPEECH_TRANSLATION_VIEW: 'speech_translation:view',

  // ==================== 智慧教室模块（契约码，禁止改名） ====================
  CLASSROOM_USE: 'classroom:use',
  CLASSROOM_VIEW: 'classroom:view',
  CLASSROOM_MANAGE: 'classroom:manage',
  CLASSROOM_VERSION_MANAGE: 'classroom:version:manage',

  // ==================== 智慧教室 V3（契约码，禁止改名） ====================
  CLASSROOM_V3_USE: 'classroom_v3:use',
  CLASSROOM_V3_VIEW: 'classroom_v3:view',
  CLASSROOM_V3_MANAGE: 'classroom_v3:manage',
  CLASSROOM_V3_VERSION_MANAGE: 'classroom_v3:version:manage',

  // ==================== CollabCore demo 模块 ====================
  DEMO_GOODS_VIEW: 'demo:goods:view',
  DEMO_GOODS_MANAGE: 'demo:goods:manage',

  // ==================== 教研模块 ====================
  TEACHING_KB_VIEW: 'teaching:kb:view',
  TEACHING_KB_MANAGE: 'teaching:kb:manage',
  TEACHING_GRAPH_VIEW: 'teaching:graph:view',
  TEACHING_GRAPH_EDIT: 'teaching:graph:edit',
  TEACHING_GRAPH_MANAGE: 'teaching:graph:manage',
  TEACHING_DESIGN_USE: 'teaching:design:use',
  TEACHING_DESIGN_VIEW: 'teaching:design:view',
  TEACHING_PPT_USE: 'teaching:ppt:use',
  TEACHING_PPT_MANAGE: 'teaching:ppt:manage',
} as const

export const PermissionGroup = {
  VIDEO_PERMISSIONS: [
    PermissionCode.VIDEO_UPLOAD,
    PermissionCode.VIDEO_SUBTITLE,
    PermissionCode.VIDEO_TRANSLATE,
    PermissionCode.VIDEO_ASSIGN,
  ],
  VIDEO_PROJECT_PERMISSIONS: [
    PermissionCode.VIDEO_UPLOAD,
    PermissionCode.VIDEO_SUBTITLE,
    PermissionCode.VIDEO_TRANSLATE,
    PermissionCode.VIDEO_ASSIGN,
  ],

  DOCUMENT_TRANSLATION_PERMISSIONS: [
    PermissionCode.DOCUMENT_UPLOAD,
    PermissionCode.DOCUMENT_TRANSLATE,
    PermissionCode.DOCUMENT_EDIT,
    PermissionCode.DOCUMENT_VIEW_RESULTS,
    PermissionCode.DOCUMENT_ASSIGN,
  ],

  PROJECT_PERMISSIONS: [
    PermissionCode.PROJECT_LIST,
    PermissionCode.PROJECT_VIEW,
    PermissionCode.PROJECT_CREATE,
    PermissionCode.PROJECT_EDIT,
    PermissionCode.PROJECT_DELETE,
    PermissionCode.PROJECT_EXPORT,
  ],

  TASK_PERMISSIONS: [
    PermissionCode.TASK_VIEW,
    PermissionCode.TASK_CREATE,
    PermissionCode.TASK_ASSIGN,
    PermissionCode.TASK_COMPLETE,
    PermissionCode.TASK_EDIT,
  ],

  SPEECH_TRANSLATION_PERMISSIONS: [
    PermissionCode.SPEECH_TRANSLATION_USE,
    PermissionCode.SPEECH_TRANSLATION_VIEW,
  ],

  CLASSROOM_PERMISSIONS: [
    PermissionCode.CLASSROOM_USE,
    PermissionCode.CLASSROOM_VIEW,
    PermissionCode.CLASSROOM_MANAGE,
    PermissionCode.CLASSROOM_VERSION_MANAGE,
  ],

  CLASSROOM_V3_PERMISSIONS: [
    PermissionCode.CLASSROOM_V3_USE,
    PermissionCode.CLASSROOM_V3_VIEW,
    PermissionCode.CLASSROOM_V3_MANAGE,
    PermissionCode.CLASSROOM_V3_VERSION_MANAGE,
  ],
  CLASSROOM_V3_ANY: [
    PermissionCode.CLASSROOM_V3_USE,
    PermissionCode.CLASSROOM_V3_VIEW,
    PermissionCode.CLASSROOM_V3_MANAGE,
    PermissionCode.CLASSROOM_V3_VERSION_MANAGE,
  ],

  SYSTEM_MANAGEMENT_PERMISSIONS: [
    PermissionCode.SYSTEM_USER,
    PermissionCode.SYSTEM_ROLE,
    PermissionCode.SYSTEM_PERMISSION,
    PermissionCode.SYSTEM_ORGANIZATION,
    PermissionCode.SYSTEM_USAGE,
    PermissionCode.SYSTEM_API,
    PermissionCode.SYSTEM_DICTIONARY,
    PermissionCode.SYSTEM_CONFIG,
  ],

  USER_MANAGEMENT_PERMISSIONS: [
    PermissionCode.SYSTEM_USER,
    PermissionCode.SYSTEM_ROLE,
  ],

  TEACHING_PERMISSIONS: [
    PermissionCode.TEACHING_KB_VIEW,
    PermissionCode.TEACHING_KB_MANAGE,
    PermissionCode.TEACHING_GRAPH_VIEW,
    PermissionCode.TEACHING_GRAPH_EDIT,
    PermissionCode.TEACHING_GRAPH_MANAGE,
    PermissionCode.TEACHING_DESIGN_USE,
    PermissionCode.TEACHING_DESIGN_VIEW,
    PermissionCode.TEACHING_PPT_USE,
    PermissionCode.TEACHING_PPT_MANAGE,
  ],
} as const

export const CONTRACT_PERMISSION_MODULES = ['classroom', 'classroom_v3', 'speech_translation'] as const

export type PermissionCodeType = typeof PermissionCode[keyof typeof PermissionCode]

export const ALL_PERMISSION_CODES: readonly PermissionCodeType[] = Object.values(PermissionCode)

export const PERMISSION_DESCRIPTIONS: Record<string, string> = {
  [PermissionCode.PROJECT_LIST]: '查看项目列表',
  [PermissionCode.PROJECT_VIEW]: '查看项目详情',
  [PermissionCode.PROJECT_CREATE]: '创建项目',
  [PermissionCode.PROJECT_EDIT]: '编辑项目',
  [PermissionCode.PROJECT_DELETE]: '删除项目',
  [PermissionCode.PROJECT_EXPORT]: '导出项目',

  [PermissionCode.VIDEO_UPLOAD]: '上传视频',
  [PermissionCode.VIDEO_SUBTITLE]: '编辑字幕',
  [PermissionCode.VIDEO_TRANSLATE]: '翻译校验',
  [PermissionCode.VIDEO_ASSIGN]: '任务指派',

  [PermissionCode.DOCUMENT_UPLOAD]: '上传文档',
  [PermissionCode.DOCUMENT_TRANSLATE]: '文档翻译',
  [PermissionCode.DOCUMENT_EDIT]: '编辑文档翻译',
  [PermissionCode.DOCUMENT_VIEW_RESULTS]: '查看翻译结果',
  [PermissionCode.DOCUMENT_ASSIGN]: '分配文档任务',

  [PermissionCode.TASK_VIEW]: '查看任务',
  [PermissionCode.TASK_CREATE]: '创建任务',
  [PermissionCode.TASK_ASSIGN]: '分配任务',
  [PermissionCode.TASK_COMPLETE]: '完成任务',
  [PermissionCode.TASK_EDIT]: '编辑任务交付物',

  [PermissionCode.SYSTEM_USER]: '用户管理',
  [PermissionCode.SYSTEM_ROLE]: '角色管理',
  [PermissionCode.SYSTEM_PERMISSION]: '权限管理',
  [PermissionCode.SYSTEM_ORGANIZATION]: '组织管理',
  [PermissionCode.SYSTEM_USAGE]: '用量管理',
  [PermissionCode.SYSTEM_API]: 'API管理',
  [PermissionCode.SYSTEM_DICTIONARY]: '数据字典',
  [PermissionCode.SYSTEM_CONFIG]: '系统配置',

  [PermissionCode.SPEECH_TRANSLATION_USE]: '使用会议翻译',
  [PermissionCode.SPEECH_TRANSLATION_VIEW]: '查看会议翻译记录',

  [PermissionCode.CLASSROOM_USE]: '使用智慧教室',
  [PermissionCode.CLASSROOM_VIEW]: '查看使用记录',
  [PermissionCode.CLASSROOM_MANAGE]: '管理智慧教室',
  [PermissionCode.CLASSROOM_VERSION_MANAGE]: '管理版本配置',

  [PermissionCode.CLASSROOM_V3_USE]: '使用智慧教室 V3',
  [PermissionCode.CLASSROOM_V3_VIEW]: '查看 V3 使用记录',
  [PermissionCode.CLASSROOM_V3_MANAGE]: '管理智慧教室 V3',
  [PermissionCode.CLASSROOM_V3_VERSION_MANAGE]: '管理 V3 版本配置',

  [PermissionCode.DEMO_GOODS_VIEW]: '查看 Demo 商品',
  [PermissionCode.DEMO_GOODS_MANAGE]: '管理 Demo 商品',

  [PermissionCode.TEACHING_KB_VIEW]: '查看教研知识库',
  [PermissionCode.TEACHING_KB_MANAGE]: '管理教研知识库',
  [PermissionCode.TEACHING_GRAPH_VIEW]: '查看专业图谱',
  [PermissionCode.TEACHING_GRAPH_EDIT]: '编辑专业图谱',
  [PermissionCode.TEACHING_GRAPH_MANAGE]: '管理专业图谱',
  [PermissionCode.TEACHING_DESIGN_USE]: '使用教案设计',
  [PermissionCode.TEACHING_DESIGN_VIEW]: '查看教案设计',
  [PermissionCode.TEACHING_PPT_USE]: '使用教案 PPT',
  [PermissionCode.TEACHING_PPT_MANAGE]: '管理教案 PPT',
}

export function getPermissionDescription(code: string): string {
  return PERMISSION_DESCRIPTIONS[code] || code
}
